import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, Link2, UserRound, Activity, Search, ScrollText, RefreshCw, Swords, Radio, Music2 } from "lucide-react";
import { api, errText } from "../lib/api";
import { Loading, Empty } from "./common";
import { Input } from "./ui/input";
import { GatewayHealth, RadioHealth } from "./GatewayAdmin";
import { ActiveCrewPicker } from "./CityUniverse2";

const when = (iso) => (iso ? new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");
const STATUS_TEXT = { unknown_license: "Link your Rockstar license", not_verified: "Link Discord to verify", suspended: "Access suspended", identity_mismatch: "Linked to another account" };
const useGet = (url) => {
  const [d, setD] = useState(null);
  const load = () => api.get(url).then(({ data }) => setD(data)).catch((e) => setD({ error: errText(e) }));
  useEffect(() => { load(); }, [url]); // eslint-disable-line
  return [d, load];
};

function Row({ ok, label, value, testid }) {
  return (
    <div className="flex items-center gap-3 py-2.5 border-t border-white/5 first:border-0" data-testid={testid}>
      {ok ? <ShieldCheck size={16} className="text-emerald-400" /> : <Link2 size={16} className="text-muted-foreground" />}
      <span className="flex-1 text-sm">{label}</span><span className={`text-xs font-mono ${ok ? "text-emerald-300" : "text-muted-foreground"}`}>{value}</span>
    </div>
  );
}

export function CitizenLinks() {
  const [d] = useGet("/virtucity/me/identity");
  if (!d) return <Loading />;
  if (d.error) return <Empty title="Identity unavailable" hint={d.error} />;
  return (
    <div className="grid lg:grid-cols-2 gap-4" data-testid="vc-citizen-links">
      <div className="surface rounded-2xl p-5">
        <div className="font-semibold mb-2">Account links</div>
        <Row ok={d.discord_linked} label="Discord" value={d.discord_linked ? d.discord_username || "Linked" : "Not linked"} testid="vc-link-discord" />
        <Row ok={d.license_linked} label="Rockstar license" value={d.license_masked || "Not linked"} testid="vc-link-license" />
        <Row ok={d.verified} label="Verified Citizen" value={d.verified ? "Active" : STATUS_TEXT[d.status] || "Not verified yet"} testid="vc-link-verified" />
        <Row ok={d.online} label="In the city" value={d.online ? "Online now" : `Last seen ${when(d.last_seen)}`} testid="vc-link-online" />
        {!d.verified && <Link to="/settings" className="inline-block mt-3 text-xs text-gold hover:underline" data-testid="vc-link-finish">Finish linking in Settings →</Link>}
      </div>
      <div className="surface rounded-2xl p-5" data-testid="vc-characters">
        <div className="font-semibold mb-2 flex items-center gap-2"><UserRound size={16} className="text-eblue" />Qbox characters</div>
        {d.characters.length === 0 ? <p className="text-xs text-muted-foreground">Characters appear here after you join VirtuCity with the linked license.</p> :
          d.characters.map((c) => (
            <div key={c.citizenid} className="flex items-center justify-between py-2 border-t border-white/5 first:border-0 text-sm" data-testid={`vc-character-${c.citizenid}`}>
              <span className="font-mono">{c.citizenid}{c.citizenid === d.active_citizenid && <span className="ml-2 text-[10px] uppercase tracking-widest text-gold">Last played</span>}</span>
              <span className="text-xs text-muted-foreground">{when(c.last_seen)}</span>
            </div>))}
      </div>
    </div>
  );
}

export function VstreetsSyncStatus() {
  const [d, load] = useGet("/virtucity/me/identity");
  if (!d) return <Loading />;
  if (d.error) return <Empty title="Sync status unavailable" hint={d.error} />;
  const last = d.sync_events || [];
  const okAt = last.find((e) => e.ok)?.at; const failed = last.find((e) => !e.ok);
  return (
    <div className="space-y-4" data-testid="vc-vstreets-sync">
      <div className="grid sm:grid-cols-3 gap-3">
        <div className="surface rounded-2xl p-4"><div className="text-xs text-muted-foreground">Active Vcrew in the city</div><div className="font-display font-bold mt-1" data-testid="vc-sync-active-crew">{d.active_crew ? `${d.active_crew.name} · ${d.active_crew.rank}` : d.crew_count > 1 ? "Pick one below" : "None"}</div>{d.active_crew && <div className="text-xs text-muted-foreground mt-1">{d.active_crew.npc_count}/{d.active_crew.npc_limit} NPC slots</div>}</div>
        <div className="surface rounded-2xl p-4"><div className="text-xs text-muted-foreground">Last successful sync</div><div className="font-semibold mt-1 text-emerald-300" data-testid="vc-sync-last-ok">{when(okAt)}</div></div>
        <div className="surface rounded-2xl p-4"><div className="text-xs text-muted-foreground">Last problem</div><div className="font-semibold mt-1 text-rose-300 text-sm" data-testid="vc-sync-last-fail">{failed ? `${failed.reason.replace(/_/g, " ")} · ${when(failed.at)}` : "None"}</div></div>
      </div>
      {d.crew_count > 1 && <ActiveCrewPicker />}
      <div className="surface rounded-2xl p-4">
        <div className="flex items-center gap-2 mb-2"><Activity size={15} className="text-eblue" /><span className="font-semibold text-sm flex-1">Recent events from VirtuCity</span><button onClick={load} className="text-muted-foreground hover:text-white" data-testid="vc-sync-refresh"><RefreshCw size={14} /></button></div>
        {last.length === 0 ? <p className="text-xs text-muted-foreground">Nothing received from the game server yet. Join VirtuCity to sync.</p> :
          last.map((e, i) => <div key={i} className="text-xs font-mono py-1 flex gap-2"><span className={e.ok ? "text-emerald-400" : "text-rose-400"}>{e.ok ? "OK" : "FAIL"}</span><span>{e.kind}</span><span className="text-muted-foreground flex-1 truncate">{e.detail || e.reason}</span><span className="text-muted-foreground">{when(e.at)}</span></div>)}
      </div>
    </div>
  );
}

export function ActivityFeed() {
  const [d] = useGet("/virtucity/activity");
  if (!d) return <Loading />;
  if (d.error) return <Empty title="Activity unavailable" hint={d.error} />;
  if (!d.items.length) return <Empty icon={Activity} title="No city activity yet" hint="Jobs, properties, vehicles, Vcrew and NPC activity appear here as they happen." />;
  return (
    <div className="surface rounded-2xl p-4 divide-y divide-white/5" data-testid="vc-activity-feed">
      {d.items.map((r, i) => (
        <div key={i} className="py-2.5 flex items-start gap-3 text-sm" data-testid={`vc-activity-${i}`}>
          {r.kind === "npc" ? <Swords size={15} className="text-rose-300 mt-0.5" /> : <Activity size={15} className="text-gold mt-0.5" />}
          <div className="flex-1 min-w-0"><div className="truncate">{r.text}</div><div className="text-[11px] text-muted-foreground">{r.type.replace(/_/g, " ")} · {when(r.at)}</div></div>
        </div>
      ))}
    </div>
  );
}

export function RadioMusicLinks() {
  return (
    <div className="grid sm:grid-cols-2 gap-3 mt-4" data-testid="vc-radio-links">
      <Link to="/music" className="surface rounded-2xl p-4 hover:border-gold/30 border border-transparent transition-colors flex items-center gap-3"><Music2 className="text-gold" /><div><div className="font-semibold">VirtuStars Music</div><div className="text-xs text-muted-foreground">Upload songs. Every public song that passes the audio check can play on VStars Radio.</div></div></Link>
      <div className="surface rounded-2xl p-4 flex items-center gap-3"><Radio className="text-eblue" /><div><div className="font-semibold">In the city</div><div className="text-xs text-muted-foreground">/vradio vstars · /vradiovol 0-100 · /vradiomute · /vradiotest</div></div></div>
    </div>
  );
}

function CitizenDirectory() {
  const [q, setQ] = useState("");
  const [d, setD] = useState(null);
  useEffect(() => { const t = setTimeout(() => api.get("/admin/virtucity/citizen-directory", { params: q ? { q } : {} }).then(({ data }) => setD(data)).catch((e) => setD({ error: errText(e) })), 250); return () => clearTimeout(t); }, [q]);
  return (
    <div className="surface-2 rounded-xl p-4" data-testid="cc-citizens">
      <div className="flex items-center gap-2 mb-3"><ShieldCheck size={16} className="text-emerald-400" /><div className="font-semibold text-sm flex-1">Citizens {d?.total != null && <span className="text-muted-foreground font-normal">· {d.total} linked</span>}</div>
        <div className="relative w-56"><Search size={13} className="absolute left-2.5 top-2.5 text-muted-foreground" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search username or Discord" className="h-8 pl-7 text-xs surface border-white/10" data-testid="cc-citizens-search" /></div></div>
      {!d ? <Loading /> : d.error ? <p className="text-xs text-rose-400">{d.error}</p> : (
        <div className="overflow-x-auto max-h-72"><table className="w-full text-xs">
          <thead><tr className="text-left text-muted-foreground"><th className="py-1 pr-3">User</th><th className="pr-3">Discord</th><th className="pr-3">License</th><th className="pr-3">Status</th><th className="pr-3">Chars</th><th>Last seen</th></tr></thead>
          <tbody>{d.citizens.length === 0 && <tr><td colSpan={6} className="py-3 text-center text-muted-foreground" data-testid="cc-citizens-empty">No citizens match “{q}”.</td></tr>}{d.citizens.map((c) => (
            <tr key={c.id} className="border-t border-white/5" data-testid={`cc-citizen-${c.id}`}>
              <td className="py-1.5 pr-3">@{c.username}</td><td className="pr-3">{c.discord_username || "—"}</td><td className="pr-3 font-mono">{c.license_masked}</td>
              <td className={`pr-3 ${c.banned || c.revoked ? "text-rose-400" : c.verified ? "text-emerald-400" : "text-amber-300"}`}>{c.banned ? "banned" : c.revoked ? "revoked" : c.verified ? "verified" : "unverified"}{c.online ? " · online" : ""}</td>
              <td className="pr-3">{c.characters}</td><td>{when(c.last_seen)}</td>
            </tr>))}</tbody>
        </table></div>
      )}
    </div>
  );
}

function AuditLog() {
  const [d, load] = useGet("/admin/virtucity/audit");
  return (
    <div className="surface-2 rounded-xl p-4" data-testid="cc-audit">
      <div className="flex items-center gap-2 mb-2"><ScrollText size={16} className="text-gold" /><div className="font-semibold text-sm flex-1">Admin audit log</div><button onClick={load} className="text-muted-foreground hover:text-white" data-testid="cc-audit-refresh"><RefreshCw size={14} /></button></div>
      {!d ? <Loading /> : d.error ? <p className="text-xs text-rose-400">{d.error}</p> : d.entries.length === 0 ? <p className="text-xs text-muted-foreground">No admin changes recorded yet.</p> :
        <div className="max-h-64 overflow-y-auto space-y-1">{d.entries.map((e, i) => <div key={i} className="text-xs font-mono flex gap-2"><span className="text-muted-foreground w-28 shrink-0">{when(e.at)}</span><span className="text-gold">@{e.admin || "?"}</span><span className="truncate">{e.method} {e.path.replace("/api", "")}{e.query ? `?${e.query}` : ""}</span></div>)}</div>}
    </div>
  );
}

export function CommandCenter() {
  return (
    <div className="space-y-4" data-testid="vc-command-center">
      <GatewayHealth />
      <CitizenDirectory />
      <RadioHealth />
      <AuditLog />
    </div>
  );
}
